import { useState, useEffect, useCallback } from "react";
import { Banknote, CheckCircle2, XCircle, AlertTriangle, RefreshCw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr, fmtDateTime, initials } from "@/lib/format";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function Deposits() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amt, setAmt] = useState({});
  const [reason, setReason] = useState("");
  const [verifyingId, setVerifyingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/deposits");
      setItems(data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const verifyDeposit = async (d, isFull) => {
    const verifiedAmount = isFull ? d.amount : (amt[d.id] != null && amt[d.id] !== "" ? Number(amt[d.id]) : d.amount);
    setVerifyingId(d.id);
    try {
      await api.post(`/deposits/${d.id}/verify`, {
        status: verifiedAmount >= d.amount ? "verified" : "partially_verified",
        verified_amount: verifiedAmount,
      });
      toast.success(verifiedAmount >= d.amount ? "Deposit fully verified" : "Partial deposit verified");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setVerifyingId(null);
    }
  };

  const rejectDeposit = async (d) => {
    if (!reason.trim()) {
      toast.error("Rejection reason is mandatory");
      return;
    }
    setVerifyingId(d.id);
    try {
      await api.post(`/deposits/${d.id}/verify`, {
        status: "rejected",
        reason: reason.trim(),
      });
      toast.success("Deposit rejected");
      setReason("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setVerifyingId(null);
    }
  };

  const pending = items.filter((d) => d.status === "pending_verification" || d.status === "submitted");
  const totalPendingAmt = pending.reduce((s, d) => s + Number(d.amount || 0), 0);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Deposits Verification"
        subtitle={`${pending.length} deposit submissions awaiting verification (${inr(totalPendingAmt)})`}
        testid="deposits-page"
        action={
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        }
      />

      {loading ? (
        <Loading label="Loading deposit submissions..." />
      ) : items.length === 0 ? (
        <EmptyState icon={Banknote} title="No deposits submitted" subtitle="Physiotherapist deposit submissions will appear here." />
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {items.map((d) => {
            const isPending = d.status === "pending_verification" || d.status === "submitted";
            return (
              <Card key={d.id} data-testid={`deposit-${d.id}`} className="p-4 flex flex-col justify-between">
                <div>
                  <div className="flex items-start gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-bk-lightGreen text-bk-green font-semibold text-sm">
                        {initials(d.physio_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-bold text-slate-800 truncate">{d.physio_name}</p>
                        <StatusBadge status={d.status} />
                      </div>
                      <p className="text-xs text-slate-400">{fmtDateTime(d.created_at)} · Mode: <span className="capitalize">{d.method}</span></p>
                      
                      <div className="grid grid-cols-3 gap-2 mt-2.5 p-2 bg-slate-50 border rounded-lg text-center">
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase">Liability</p>
                          <p className="font-semibold text-xs tabular-nums text-slate-700">{inr(d.expected)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase">Submitted</p>
                          <p className="font-bold text-xs text-bk-green tabular-nums">{inr(d.amount)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase">Verified</p>
                          <p className="font-bold text-xs text-slate-900 tabular-nums">{inr(d.deposited_amount)}</p>
                        </div>
                      </div>

                      {d.note && <p className="text-xs text-slate-500 mt-2">Note: {d.note}</p>}
                      {d.reason && <p className="text-xs text-amber-700 mt-1">Variance Reason: {d.reason}</p>}
                      {d.reject_reason && <p className="text-xs text-red-600 mt-1">Rejected: {d.reject_reason}</p>}
                    </div>
                  </div>
                </div>

                {isPending && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
                    <Input
                      type="number"
                      placeholder={`Verify amt (${inr(d.amount)})`}
                      value={amt[d.id] ?? ""}
                      onChange={(e) => setAmt((x) => ({ ...x, [d.id]: e.target.value }))}
                      className="h-8 text-xs flex-1 bg-white"
                      data-testid={`deposit-amt-${d.id}`}
                    />
                    <Button
                      size="sm"
                      onClick={() => verifyDeposit(d, false)}
                      disabled={verifyingId === d.id}
                      data-testid={`approve-deposit-${d.id}`}
                      className="h-8 text-xs bg-bk-green hover:bg-emerald-700 text-white"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Verify
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`reject-deposit-${d.id}`}>
                          <XCircle className="h-3.5 w-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Reject deposit submission?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Please provide a reason so the physiotherapist can correct and resubmit.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className="space-y-1.5 py-2">
                          <Label>Rejection Reason *</Label>
                          <Input
                            value={reason}
                            placeholder="e.g. Bank credit not received / Amount mismatch"
                            onChange={(e) => setReason(e.target.value)}
                          />
                        </div>
                        <AlertDialogFooter>
                          <AlertDialogCancel onClick={() => setReason("")}>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => rejectDeposit(d)} className="bg-red-600 hover:bg-red-700 text-white">
                            Confirm Rejection
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
